import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { track } from "../lib/analytics";

export default function PickupPicker({ value, onChange }) {
  const [locations, setLocations] = useState(null);
  const [slots, setSlots] = useState([]);
  const [locId, setLocId] = useState(value?.location_id || "");

  useEffect(() => { api.get("/locations?include_test=true").then((r) => setLocations(r.data)); }, []);
  useEffect(() => {
    if (!locId) { setSlots([]); return; }
    api.get(`/locations/${locId}/slots`).then((r) => setSlots(r.data.slots));
  }, [locId]);

  const loc = (locations || []).find((l) => l.id === locId);
  const publicLocs = (locations || []).filter((l) => l.public !== false);
  const testLocs = (locations || []).filter((l) => l.public === false);
  if (locations === null) {
    return <p className="text-sm text-[#171715]/60" role="status" data-testid="pickup-loading">Loading pickup locations…</p>;
  }

  const LocButton = ({ l }) => (
    <button key={l.id} data-testid={`pickup-location-option-${l.id}`} disabled={!l.bookable}
      onClick={() => { setLocId(l.id); onChange(null); }}
      className={`text-left card-paper p-3 min-h-[44px] transition-colors ${locId === l.id ? "border-[#A35847] ring-1 ring-[#A35847]" : l.bookable ? "hover:border-[#A35847]/50" : "opacity-60 cursor-not-allowed"}`}>
      <span className="font-medium text-sm block">{l.name}</span>
      <span className="text-xs text-[#171715]/60 block">{l.neighborhood}</span>
      {l.status === "active_partner" && !l.bookable && <span className="text-[10px] font-mono uppercase text-[#A35847]">Active partner — scheduling opens soon</span>}
      {l.public === false && <span className="text-[10px] font-mono uppercase text-[#9c3b2a]">Sample location — test checkout only</span>}
    </button>
  );

  return (
    <div className="space-y-4">
      <div>
        <p className="eyebrow mb-2">1 · Pickup location</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {publicLocs.map((l) => <LocButton key={l.id} l={l} />)}
        </div>
        {testLocs.length > 0 && (
          <div className="mt-3">
            <p className="text-[11px] font-mono uppercase text-[#9c3b2a] mb-2">Test environment only — removed automatically at launch</p>
            <div className="grid sm:grid-cols-2 gap-3">{testLocs.map((l) => <LocButton key={l.id} l={l} />)}</div>
          </div>
        )}
        {locations.length === 0 && <p className="text-sm text-[#171715]/60">No pickup locations available.</p>}
      </div>
      {locId && (
        <div>
          <p className="eyebrow mb-2">2 · Pickup window (America/Chicago · {loc?.cutoff_hours ? `orders lock ${loc.cutoff_hours}h before` : "at least 24h notice"})</p>
          <div className="grid sm:grid-cols-2 gap-2" data-testid="pickup-slot-list">
            {slots.map((s) => {
              const sel = value && value.window_id === s.window_id && value.date === s.date;
              return (
                <button key={`${s.window_id}-${s.date}`} data-testid={`pickup-slot-${s.window_id}-${s.date}`} disabled={!s.available}
                  onClick={() => { onChange({ location_id: locId, location_name: loc?.name, window_id: s.window_id, date: s.date, label: s.label, cutoff_at: s.cutoff_at }); track("pickup_selected", { location_id: locId }); }}
                  className={`text-left card-paper p-3 min-h-[44px] text-sm ${sel ? "border-[#A35847] ring-1 ring-[#A35847]" : s.available ? "hover:border-[#A35847]/50" : "opacity-40 cursor-not-allowed"}`}>
                  <span className="font-medium block">{s.date}</span>
                  <span className="text-xs text-[#171715]/70">{s.label} · {s.available ? `${s.remaining} spots left` : "Full"}</span>
                </button>
              );
            })}
            {slots.length === 0 && <p className="text-sm text-[#171715]/60">No bookable windows yet at this location.</p>}
          </div>
        </div>
      )}
      <p className="text-xs text-[#171715]/55">One pickup location per order — a second location needs a separate order. Dallas–Fort Worth delivery is also available by arrangement (see the Pickup page).</p>
    </div>
  );
}
